import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Alert, Button, Stack } from 'react-bootstrap';
import { BsFillTrash3Fill as DeleteIcon } from 'react-icons/bs';

import api from 'api';
import useFeedback from 'hooks/useFeedback';
import useReports from 'hooks/useReports';
import useTableState from 'hooks/useTableState';
import { DEFAULT_PAGE_SIZE } from 'utils/constants';

import Modal from 'components/elements/BasicModal';
import Table from 'components/elements/Table';
import SectionWrapper from 'components/layout/SectionWrapper';
import ReportManageMenu from 'components/templates/menus/ReportManageMenu';

const ReportList = () => {
  const navigate = useNavigate();
  const { setSuccess, setError } = useFeedback();
  const [toDelete, setToDelete] = React.useState(null);
  const {
    page, handlePageChange, sortBy, sortOrder, handleSortChange,
  } = useTableState('name', 'asc', {});

  const {
    errorsData, fetchReports, isLoadingReports, reports,
  } = useReports();

  const refreshList = React.useCallback(() => {
    fetchReports({
      Page: page,
      PageSize: DEFAULT_PAGE_SIZE,
      Sort: sortBy,
      SortDirection: sortOrder,
    });
  }, [fetchReports, page, sortBy, sortOrder]);

  const handleDelete = React.useCallback(() => {
    api
      .deleteDashboard(toDelete.id)
      .then(() => {
        setToDelete(null);
        setSuccess('Deleted with success');
        refreshList();
      })
      .catch(() => setError('Error deleting report'));
  }, [toDelete, refreshList, setError, setSuccess]);

  const renderDeleteModal = React.useMemo(
    () => (
      <Modal
        isOpen={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        title={`Are you sure you want to delete "${toDelete?.name}"?`}
        onConfirm={handleDelete}
      />
    ),
    [toDelete, handleDelete],
  );

  const handleDeletePrompt = React.useCallback((event, item) => {
    event.stopPropagation();
    setToDelete(item);
  }, []);

  const deleteButton = React.useCallback(
    (item) => (
      <Button size="sm" onClick={(e) => handleDeletePrompt(e, item)}>
        <DeleteIcon />
      </Button>
    ),
    [handleDeletePrompt],
  );

  const columns = React.useMemo(
    () => [
      {
        id: 'name',
        label: 'Name',
      },
      {
        id: 'delete',
        nonSortable: true,
        override: deleteButton,
        columnStyle: { textAlign: 'right' },
      },
    ],
    [deleteButton],
  );

  React.useEffect(() => {
    refreshList();
  }, [refreshList]);

  return (
    <SectionWrapper>
      <Stack
        direction="horizontal"
        gap={3}
        className="align-items-start"
        style={{ minWidth: 0 }}
      >
        <div style={{ flexShrink: 0 }}>
          <ReportManageMenu />
        </div>
        <Stack
          gap={3}
          className="align-items-start"
          style={{ flex: 1, minWidth: 0 }}
        >
          <Button as={Link} variant="secondary" to="new">
            New Report
          </Button>
          {renderDeleteModal}
          {errorsData ? (
            <Alert variant="danger">{errorsData}</Alert>
          ) : (
            <Table
              columns={columns}
              items={reports.data}
              handlePageChange={handlePageChange}
              handleOrderChange={handleSortChange}
              order={sortBy}
              orderDirection={sortOrder}
              pagination={{
                page,
                total: reports.count || 0,
                limit: reports.pageSize || 0,
              }}
              onClickRow={({ id }) => navigate(id)}
              isLoading={isLoadingReports}
            />
          )}
        </Stack>
      </Stack>
    </SectionWrapper>
  );
};

export default ReportList;
